import { Variation, VariableDefinitionTypeEnum, Rule } from 'types/backend';
import { ReadonlyPart } from 'types/utilities';

export const FlagEntityTypes: {
  [key: string]: { readableName: string; type: string };
} = {
  any: {
    readableName: 'Any',
    type: 'any',
  },
  flag: {
    readableName: 'Flag',
    type: 'flag',
  },
  metric_set: {
    readableName: 'Metrics',
    type: 'metrics',
  },
  permission: {
    readableName: 'Permission',
    type: 'permission',
  },
  rule: {
    readableName: 'Rule',
    type: 'rule',
  },
  ruleset: {
    readableName: 'Ruleset',
    type: 'ruleset',
  },
  variable: {
    readableName: 'Variable',
    type: 'variable',
  },
  variation: {
    readableName: 'Variation',
    type: 'variation',
  },
};

export const FlagManagerPage = {
  APIVALUES: {
    id: 'APIVALUES',
    name: 'API Values',
    sideNavPosition: 3,
    slug: 'apivalues',
  },
  HISTORY: {
    id: 'HISTORY',
    name: 'History',
    sideNavPosition: 4,
    slug: 'history',
  },
  RULES: {
    id: 'RULES',
    name: 'Rules',
    sideNavPosition: 0,
    slug: 'rules',
  },
  SCHEDULES: {
    id: 'SCHEDULES',
    name: 'Scheduled Changes',
    sideNavPosition: 5,
    slug: 'schedules',
  },
  SETTINGS: {
    id: 'SETTINGS',
    name: 'Settings',
    sideNavPosition: 6,
    slug: 'settings',
  },
  VARIABLES: {
    id: 'VARIABLES',
    name: 'Variables',
    sideNavPosition: 1,
    slug: 'variables',
  },
  VARIATIONS: {
    id: 'VARIATIONS',
    name: 'Variations',
    sideNavPosition: 2,
    slug: 'variations',
  },
};

export const VariableTypes = {
  [VariableDefinitionTypeEnum.Boolean]: 'Boolean',
  [VariableDefinitionTypeEnum.String]: 'String',
  [VariableDefinitionTypeEnum.Integer]: 'Integer',
  [VariableDefinitionTypeEnum.Double]: 'Double',
  [VariableDefinitionTypeEnum.Json]: 'JSON',
};

export const VariableTypesIcon = {
  [VariableDefinitionTypeEnum.Boolean]: 'toggle-off-thin',
  [VariableDefinitionTypeEnum.String]: 'text-thin',
  [VariableDefinitionTypeEnum.Integer]: 'input-numeric-thin',
  [VariableDefinitionTypeEnum.Double]: '00-thin',
  [VariableDefinitionTypeEnum.Json]: 'brackets-curly-thin',
};
export const VariableTypesDesc = {
  [VariableDefinitionTypeEnum.Boolean]: 'Use this for true/false values',
  [VariableDefinitionTypeEnum.String]: 'Use this for text values',
  [VariableDefinitionTypeEnum.Integer]: 'Use this whole numbers',
  [VariableDefinitionTypeEnum.Double]: 'Use this for decimal values',
  [VariableDefinitionTypeEnum.Json]: 'Use this for structured data',
};

export const BRAINSTORM = {
  variable: {
    description:
      'Hello! Happy to help you brainstorm variable ideas. What hypothesis would you like to test?',
    hypothesisExample:
      "If we add a magnifying glass icon next to the search feature then we'll see a 3% increase in search engagement because the feature will be more visually distinct from the rest of the global navigation.",
    hypothesisTip:
      'Share what problem you are trying to solve, what solution you have considered and the expected impact. ',
  },
  variation: {
    description:
      'Hello! Happy to help you brainstorm variation ideas. What hypothesis would you like to test?',
    hypothesisExample:
      "If we add a magnifying glass icon next to the search feature then we'll see a 3% increase in search engagement because the feature will be more visually distinct from the rest of the global navigation.",
    hypothesisTip:
      'Share what problem you are trying to solve, what solution you have considered and the expected impact. ',
  },
};

export const ApprovalActionsTooltip = {
  audiences: ['Add/remove/modify an audience from a rule'],
  metrics: ['Add/remove/modify a metric from a rule'],
  ramp_perc: ['Modify traffic allocation percentage'],
  rules: ['Run/Pause/Conclude rule', 'Delete rule'],
  ruleset: [
    'Run/Pause ruleset',
    'Change order of rules',
    'Modify "Everyone Else" variation',
  ],
  variations: [
    'Add/remove/modify variation selections',
    'Modify variation traffic distribution percentages',
  ],
};
export const PendingRequestErrorMessage =
  'Cannot remove the flag or modify actions that require approval. There are pending change requests associated with one of the flags in the update request. Please resolve or withdraw these requests before proceeding.';
export const StatusMap = {
  Conclude: 'Concluded',
  Pause: 'Paused',
  Run: 'Running',
};
export const DefaultVariableConfigs = {
  [VariableDefinitionTypeEnum.Boolean]: {
    default_value: 'true',
  },
  [VariableDefinitionTypeEnum.String]: {
    default_value: '',
  },
  [VariableDefinitionTypeEnum.Integer]: {
    default_value: '0',
  },
  [VariableDefinitionTypeEnum.Double]: {
    default_value: '0.0',
  },
  [VariableDefinitionTypeEnum.Json]: {
    default_value: '{}',
  },
};

export const READONLY_VARIATION_FIELDS: Array<keyof ReadonlyPart<Variation>> = [
  'id',
  'urn',
  'environment_usage_count',
  'created_time',
  'updated_time',
  'enabled',
  'url',
  'fetch_flag_url',
  'update_url',
  'archive_url',
  'unarchive_url',
  'delete_url',
];

export const READONLY_RULE_FIELDS: Array<keyof ReadonlyPart<Rule>> = [
  'id',
  'urn',
  'created_time',
  'updated_time',
  'fetch_results_ui_url',
  'report_key',
];

export const MAX_RULE_PERCENTAGE_BASIS_POINTS = 10000;

export const ENTIRE_FLAG_SELECTION_VALUE = '[entire_flag]';

export const DUPLICATE_SCHEDULE = 'duplicate_schedule';

export const ALL_ENVIRONMENT_FILTER = { key: '[all]', name: 'All' };

export const ALL_RULES_FILTER = { key: '[all]', name: 'All' };

export const MAX_RULES = 100;

const constants = {
  DefaultVariableConfigs,
  DUPLICATE_SCHEDULE,
  ENTIRE_FLAG_SELECTION_VALUE,
  FlagEntityTypes,
  FlagManagerPage,
  MAX_RULE_PERCENTAGE_BASIS_POINTS,
  READONLY_RULE_FIELDS,
  READONLY_VARIATION_FIELDS,
  VariableTypes,
};

export default constants;
